import { Button } from "@admin/components/atoms/Button";
import { ErrorMessage } from "@admin/components/atoms/ErrorMessage";
import { Input } from "@admin/components/atoms/Input";
import { Label } from "@admin/components/atoms/Input/Label";
import { Modal, ModalProps } from "@admin/components/atoms/Modal";
import { DayPicker } from "@admin/components/molecules/Picker";
import {
  AddShift,
  addShift,
  dateToISO,
  LocationUser,
} from "@enos-monorepo/lib";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { addDays, set, subDays } from "date-fns";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { z } from "zod";

interface PersonnelViewAddPastShiftModalProps extends ModalProps {
  selectedPersonnel?: LocationUser;
  locationId: string;
  companyId: string;
}

export function PersonnelViewAddPastShiftModal({
  selectedPersonnel,
  companyId,
  locationId,
  onClose,
  ...props
}: PersonnelViewAddPastShiftModalProps) {
  const { t } = useTranslation();
  const [calculatedTime, setCalculatedTime] = useState(0);
  const [selectedDay, setSelectedDay] = useState<Date>(subDays(new Date(), 1));

  const shiftSchema = z.object({
    startPlanned: z
      .string()
      .regex(/^([01]\d|2[0-3]):([0-5]\d)$/, t("shift.Invalid time format"))
      .optional()
      .or(z.literal("")),
    endPlanned: z
      .string()
      .regex(/^([01]\d|2[0-3]):([0-5]\d)$/, t("shift.Invalid time format"))
      .optional()
      .or(z.literal("")),
    startActual: z
      .string()
      .regex(/^([01]\d|2[0-3]):([0-5]\d)$/, t("shift.Invalid time format")),
    endActual: z
      .string()
      .regex(/^([01]\d|2[0-3]):([0-5]\d)$/, t("shift.Invalid time format")),
    totalTime: z
      .number()
      .min(0, t("shift.Total time must be greater than or equal to zero"))
      .refine((value) => value >= 0, {
        message: t("shift.Total time must be greater than or equal to zero"),
      }),
    breakDuration: z
      .number()
      .min(0, t("shift.Break duration must be greater than or equal to zero")),
  });

  type PastShiftPersonnelFormValues = z.infer<typeof shiftSchema>;

  const {
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: state,
  } = useForm<PastShiftPersonnelFormValues>({
    resolver: zodResolver(shiftSchema),
  });
  const startActual = watch("startActual");
  const endActual = watch("endActual");
  const breakDuration = watch("breakDuration");

  const { mutate, isPending } = useMutation({
    mutationFn: (shift: AddShift) => addShift(shift),
    onSuccess: () => {
      toast.success(t("shift.Shifts have been planned"));
      queryClient.invalidateQueries({ queryKey: ["shifts"] });
      reset();
      onClose?.();
    },
    onError: () => {
      toast.error(t("shift.Unable to plan shifts"));
    },
  });

  const handleClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  useEffect(() => {
    setValue("totalTime", parseFloat(calculatedTime.toFixed(2)));
  }, [calculatedTime, setValue]);

  const calculateTimeDiff = useCallback(() => {
    if (startActual && endActual) {
      const startTime = new Date(
        `${selectedDay?.toDateString()} ${startActual}`,
      );
      const endTime = new Date(`${selectedDay?.toDateString()} ${endActual}`);

      if (endTime < startTime) {
        endTime.setDate(endTime.getDate() + 1);
      }

      const diff = (endTime.getTime() - startTime.getTime()) / 1000 / 60 / 60;

      setCalculatedTime(diff - breakDuration / 60);
    }
  }, [startActual, endActual, breakDuration, selectedDay]);

  const onSubmit = useCallback(
    (data: PastShiftPersonnelFormValues) => {
      let endTime;
      let startTime;

      const [actualHours, actualMinutes] = data.startActual
        .split(":")
        .map(Number);
      const [actualEndHours, actualEndMinutes] = data.endActual
        .split(":")
        .map(Number);

      if (data.startPlanned && data.endPlanned) {
        const [startHours, startMinutes] = data.startPlanned
          .split(":")
          .map(Number);
        const [endHours, endMinutes] = data.endPlanned.split(":").map(Number);

        startTime = set(selectedDay || new Date(), {
          hours: startHours,
          minutes: startMinutes,
          seconds: 0,
          milliseconds: 0,
        });

        endTime = set(selectedDay || new Date(), {
          hours: endHours,
          minutes: endMinutes,
          seconds: 0,
          milliseconds: 0,
        });

        if (endTime < startTime) {
          endTime = addDays(endTime, 1);
        }
      }

      const actualStartTime = set(selectedDay || new Date(), {
        hours: actualHours,
        minutes: actualMinutes,
        seconds: 0,
        milliseconds: 0,
      });

      let actualEndTime = set(selectedDay || new Date(), {
        hours: actualEndHours,
        minutes: actualEndMinutes,
        seconds: 0,
        milliseconds: 0,
      });

      if (actualEndTime < actualStartTime) {
        actualEndTime = addDays(actualEndTime, 1);
      }

      const payload: AddShift = {
        plannedStartDate: startTime && dateToISO(startTime),
        plannedEndDate: endTime && dateToISO(endTime),
        workStartDate: dateToISO(actualStartTime),
        workEndDate: dateToISO(actualEndTime),
        breakDuration: data.breakDuration,
        companyId: companyId,
        companyLocationId: locationId,
        userId: selectedPersonnel?.userId ?? "",
      };

      mutate(payload);
    },
    [selectedDay, companyId, locationId, selectedPersonnel?.userId, mutate],
  );

  const queryClient = useQueryClient();
  return (
    <Modal
      {...props}
      title={t("shift.Manually Add a Past Shift With Name", {
        name: `${selectedPersonnel?.firstName} ${selectedPersonnel?.lastName}`,
      })}
      actionButtons={
        <>
          <Button variant="secondary" onClick={handleClose}>
            {t("common.Discard")}
          </Button>
          <Button loading={isPending} onClick={handleSubmit(onSubmit)}>
            {t("shift.Add a Past shift")}
          </Button>
        </>
      }
      onClose={handleClose}
    >
      <form>
        <div className="flex flex-col">
          <Label title={t("shift.Select Date")}>
            <DayPicker
              setSelectedDay={setSelectedDay}
              selectedDay={selectedDay}
              disabledDays={{ after: subDays(new Date(), 1) }}
            />
          </Label>
        </div>

        <div className="flex flex-col">
          <Label title={t("shift.Planned Times")}>
            <div className="flex flex-row gap-2.5">
              <Input
                placeholder={t("shift.Start")}
                type="time"
                {...register("startPlanned")}
                name="startPlanned"
              />
              <Input
                {...register("endPlanned")}
                type="time"
                name="endPlanned"
                placeholder={t("shift.End")}
              />
            </div>
          </Label>
          <ErrorMessage
            message={` ${state.errors.startPlanned?.message ?? ""} ${state.errors.endPlanned?.message ?? ""}`}
          />
        </div>
        <div className="flex flex-col">
          <Label title={t("shift.Actual Times")} required>
            <div className="flex flex-row gap-2.5">
              <Input
                placeholder={t("shift.Start")}
                type="time"
                {...register("startActual")}
                name="startActual"
                onBlur={calculateTimeDiff}
              />
              <Input
                {...register("endActual")}
                type="time"
                name="endActual"
                placeholder={t("shift.End")}
                onBlur={calculateTimeDiff}
              />
            </div>
          </Label>
          <ErrorMessage
            message={`${state.errors.startActual?.message ?? ""} ${state.errors.endActual?.message ?? ""}`}
          />
        </div>

        <div className="flex flex-col">
          <Label title={t("shift.Break Duration")}>
            <Input
              type="number"
              {...register("breakDuration", { valueAsNumber: true })}
              onBlur={calculateTimeDiff}
              placeholder={t("shift.Break Duration")}
              defaultValue={0}
            />
          </Label>
          <ErrorMessage message={state.errors.breakDuration?.message} />
        </div>

        <div className="flex flex-col">
          <Label title={t("shift.Calculated Time")}>
            <Input
              {...register("totalTime", { valueAsNumber: true })}
              name="totalTime"
              value={calculatedTime.toFixed(2)}
              defaultValue={calculatedTime.toFixed(2)}
              readOnly
            />
          </Label>
          <ErrorMessage message={state.errors.totalTime?.message} />
        </div>
      </form>
    </Modal>
  );
}
